// Shared spawn weights for board deals and card packs.
// Endless: alpha 0.2% each, gems 0.1% each, founders 0.01% each, remaining mass on base cards.
// Base cards: OriginalBaseCorgi models are 5× as likely as light/dark variations.
// Story boards omit special variants (allowSpecialVariants: false).

import { getCorgiBaseModel } from "./corgiTraits";

export const FOIL_CHANCE = 0.01;
export const ALPHA_APPEARANCE_CHANCE = 0.002;
export const GEM_APPEARANCE_CHANCE = 0.001;
export const FOUNDER_APPEARANCE_CHANCE = 0.0001;
export const BASE_ORIGINAL_MODEL_WEIGHT = 5;
export const BASE_VARIATION_MODEL_WEIGHT = 1;

const SPECIAL_RARITIES = new Set(["alpha", "gem", "founder"]);
const BASE_VARIATION_MODELS = new Set(["LightBaseCorgi", "ChocolateBaseCorgi"]);

const normalizeId = (id) => {
    const n = Number(id);
    return Number.isFinite(n) ? n : id;
};

export function getBaseModelWeightMultiplier(cardId) {
    const model = getCorgiBaseModel(cardId);
    if (model === "OriginalBaseCorgi") {
        return BASE_ORIGINAL_MODEL_WEIGHT;
    }
    if (BASE_VARIATION_MODELS.has(model)) {
        return BASE_VARIATION_MODEL_WEIGHT;
    }
    return BASE_VARIATION_MODEL_WEIGHT;
}

export function getCardAppearanceWeight(
    card,
    { standardTotalChance = 0, baseWeightUnitSum = 0 } = {}
) {
    if (card?.rarity === "alpha") {
        return ALPHA_APPEARANCE_CHANCE;
    }

    if (card?.rarity === "gem") {
        return GEM_APPEARANCE_CHANCE;
    }

    if (card?.rarity === "founder") {
        return FOUNDER_APPEARANCE_CHANCE;
    }

    if (baseWeightUnitSum <= 0 || standardTotalChance <= 0) {
        return 0;
    }

    const multiplier = getBaseModelWeightMultiplier(card?.id);
    return (standardTotalChance * multiplier) / baseWeightUnitSum;
}

export function buildWeightedCardPool(
    catalog = [],
    { allowSpecialVariants = true, excludeIds = [] } = {}
) {
    const exclude = new Set(
        (excludeIds || []).map((id) => String(normalizeId(id)))
    );
    const alphaCards = allowSpecialVariants
        ? catalog.filter((card) => card.rarity === "alpha")
        : [];
    const gemCards = allowSpecialVariants
        ? catalog.filter((card) => card.rarity === "gem")
        : [];
    const founderCards = allowSpecialVariants
        ? catalog.filter((card) => card.rarity === "founder")
        : [];
    const standardCards = catalog.filter(
        (card) => !SPECIAL_RARITIES.has(card.rarity)
    );

    const specialTotalChance =
        alphaCards.length * ALPHA_APPEARANCE_CHANCE +
        gemCards.length * GEM_APPEARANCE_CHANCE +
        founderCards.length * FOUNDER_APPEARANCE_CHANCE;
    const standardTotalChance = allowSpecialVariants
        ? Math.max(0, 1 - specialTotalChance)
        : 1;
    const baseWeightUnitSum = standardCards.reduce(
        (sum, card) => sum + getBaseModelWeightMultiplier(card.id),
        0
    );

    const poolCards = allowSpecialVariants ? catalog : standardCards;
    const preferredPool = poolCards.filter(
        (card) => !exclude.has(String(normalizeId(card.id)))
    );

    return {
        standardTotalChance,
        baseWeightUnitSum,
        poolCards,
        preferredPool,
        sourcePool: preferredPool,
    };
}

/**
 * Weighted draw without replacement. Odds match Endless (or Story) boards.
 */
export function drawWeightedCards(
    catalog = [],
    count = 1,
    {
        allowSpecialVariants = true,
        excludeIds = [],
        random = Math.random,
    } = {}
) {
    const num = Math.max(0, Math.floor(Number(count) || 0));
    const built = buildWeightedCardPool(catalog, {
        allowSpecialVariants,
        excludeIds,
    });
    const sourcePool =
        built.preferredPool.length >= num
            ? built.preferredPool
            : built.poolCards;

    const weightedPool = sourcePool
        .map((card) => ({
            card,
            weight: getCardAppearanceWeight(card, {
                standardTotalChance: built.standardTotalChance,
                baseWeightUnitSum: built.baseWeightUnitSum,
            }),
        }))
        .filter((item) => item.weight > 0);

    const selectedCards = [];

    while (selectedCards.length < num && weightedPool.length > 0) {
        const totalWeight = weightedPool.reduce(
            (sum, item) => sum + item.weight,
            0
        );
        let roll = random() * totalWeight;
        let selectedIndex = 0;

        for (let i = 0; i < weightedPool.length; i += 1) {
            roll -= weightedPool[i].weight;
            if (roll <= 0) {
                selectedIndex = i;
                break;
            }
        }

        const selectedItems = weightedPool.splice(selectedIndex, 1);
        selectedCards.push(selectedItems[0].card);
    }

    return selectedCards;
}
